import React, { useEffect, useState } from 'react';
import {
    Slider,
    TextArea,
    Button,
    Toggle
} from '@kaleyra/kds';
import Styles from "../../Notifications.module.scss";
import { useTranslation } from 'react-i18next';
import infoIcon from '../../../../../assets/infoIconBlack.svg';
import { Spinner } from '@kaleyra/emmy';
import { maxEmailLimit } from '../../../../../constants';
import { validateEmail } from '../../../../../utils';
import { useSelector } from 'react-redux';
import { IStateType } from '../../../../../store/models/root.interface';

export type MapStatePropsType = {
    sidebarVisibility: boolean;
    params: any;
};

export type DispatchPropsType = {
    onClose: () => void;
    onSave: (data: any, callBack: any) => void;
    onLoad: () => void;
};

interface BooleanFrequencies {
    [key: string]: boolean
};

interface StringFrequencies {
    [key: string]: string
}

const BillingAlertConfig: React.FC<MapStatePropsType & DispatchPropsType> = ({
    onClose,
    onLoad,
    onSave,
    params,
    sidebarVisibility
}) => {

    const { t } = useTranslation();
    const [checkedFrequencies, setCheckedFrequencies] = useState<BooleanFrequencies>({});
    const [emails, setEmails] = useState<StringFrequencies>({});
    const [emailErrors, setEmailErrors] = useState<StringFrequencies>({});

    const [saving, setSaving] = useState(false);
    const profileDetails = useSelector((state: IStateType) => state.profile);
    const { email } = profileDetails.fullProfile;

    const { REPORT_KEY, data, frequencies } = params;

    useEffect(() => {

        if (data?.[REPORT_KEY]) {

            let tempFrequencies: BooleanFrequencies = {};
            let tempEmails: StringFrequencies = {};
            let freqDisp = '';

            data?.[REPORT_KEY]?.map((item: any) => {
                tempEmails[item?.report_frequency] = item?.emails || email;
                if (item?.status === 1) {
                    tempFrequencies[item?.report_frequency] = true;
                    freqDisp = freqDisp ? `${freqDisp}, ${t(item?.report_frequency)}` : t(item?.report_frequency);
                }
            })

            setCheckedFrequencies({ ...tempFrequencies });
            setEmails({ ...tempEmails });

        } else {
            setEmails({ daily: email, weekly: email, monthly: email });
            setEmailErrors({ daily: '', weekly: '', monthly: '' });
            setCheckedFrequencies({ daily: false, weekly: false, monthly: false });
        }

    }, []);

    const handleOnSave = () => {
        setSaving(true);
        let valueArray: any = [];
        let freqDisp = '';
        for (let i = 0; i < frequencies.length; i++) {
            if (checkedFrequencies[frequencies[i]]) {
                valueArray.push({
                    "report_frequency": frequencies[i],
                    "emails": emails[frequencies[i]],
                    "report_type": REPORT_KEY === 'credit_history' ? 2 : 1,
                    "status": 1
                })
                freqDisp = freqDisp ? `${freqDisp}, ${t(frequencies[i])}` : t(frequencies[i]);
            } else {
                valueArray.push({
                    "report_frequency": frequencies[i],
                    "emails": emails[frequencies[i]],
                    "report_type": REPORT_KEY === 'credit_history' ? 2 : 1,
                    "status": 0
                })
            }
        }
        onSave({ data: valueArray }, (resp: any, success: boolean) => {
            if (success) {
                onLoad();
                onClose();
            } else {
                setSaving(false);
            }
        })
    }

    const handleChkOnChange = (e: any, frequency: string) => {
        setCheckedFrequencies({ ...checkedFrequencies, [frequency]: e.target.checked });
        setEmails({ ...emails });
        setEmailErrors({ ...emailErrors, [frequency]: '' });
    };

    const handleOnBlur = (frequency: string) => {
        let errMsg = validateCheck(frequency);

        if (errMsg) {
            setEmailErrors({ ...emailErrors, [frequency]: errMsg });
        } else {
            setEmailErrors({ ...emailErrors, [frequency]: '' });
        }

        return;
    };

    const verifyValidEmail = (emailStr: string) => {
        let emails = emailStr.split(",");
        const duplicateAddresess = emails.filter((item: any, index: number) => emails.indexOf(item) !== index);
        if (emails.length > maxEmailLimit) {
            return t("KLR02734", { limit: maxEmailLimit });
        }
        if (duplicateAddresess.length > 0) {
            return t("KLR02735");
        }
        for (let i = 0; i < emails.length; i++) {
            if (!validateEmail(emails[i])) {
                return t('verifyValidEmail');
            }
        }
        return;
    };

    const validateCheck = (field: string) => {

        for (let i = 0; i < frequencies.length; i++) {
            if (field === frequencies[i]) {
                if (emails[frequencies[i]].trim() === '') {
                    return t('titleReq');
                }

                return verifyValidEmail(emails[frequencies[i]]);
            }
        }

        return;
    };

    const validateFields = (field: string): 'error' | undefined => {
        if (validateCheck(field)) {
            return "error";
        }
        return undefined;
    };

    const setErrors = (field: string) => {
        let errMsg = validateCheck(field);
        if (errMsg) {
            return (
                <div className={Styles.errorClass}>
                    {errMsg}
                </div>
            );
        }
    };

    const checkSaveButtonEnabled = () => {
        if (saving) {
            return true;
        }

        let countFalses = 0;

        for (let i = 0; i < frequencies.length; i++) {
            if (checkedFrequencies[frequencies[i]]) {
                if (validateFields(frequencies[i])) {
                    return true;
                }
            } else {
                countFalses++;
            }
        }

        if (countFalses === frequencies.length) {
            return true;
        }
    }

    const getFooter = () => {

        return (
            <div className={Styles.actionItems}>
                <div className={Styles.cancelBtnWrapper}>
                    <Button type="default" className={Styles.cancelBtn} onClick={() => { onClose(); onLoad() }}>
                        {t('mainCancel')}
                    </Button>
                </div>
                <div className={Styles.saveBtnWrapper}>
                    <Button
                        className={Styles.saveBtn}
                        type="primary"
                        onClick={handleOnSave}
                        disabled={checkSaveButtonEnabled()}
                    >
                        {saving ? <Spinner margin={"0.5rem"} color='#636C75' /> : t('mainSave')}
                    </Button>
                </div>
            </div>
        )
    };

    const getContent = () => {

        return (
            <>
                {frequencies.map((frequency: any, key: number) => {
                    return (
                        <div key={key} className={Styles.smsReportWiseFreq}>
                            <div className={Styles.dF} style={{ marginTop: 16, marginBottom: 16 }}>
                                <div style={{ width: '90%' }}>
                                    <div className={Styles.notifTitle}>{t(frequency)}</div>
                                    <p className={Styles.notifDesc}> {t(frequency + "ReportDesc")} </p>
                                </div>
                                <Toggle
                                    className={Styles.channelCheckbox}
                                    checked={checkedFrequencies[frequency]}
                                    onChange={(event: any) => handleChkOnChange(event, frequency)}
                                />
                            </div>
                            {checkedFrequencies[frequency] ?
                                <>
                                    <TextArea
                                        placeholder={t('emailIds')}
                                        statusType={emailErrors[frequency] ? validateFields(frequency) : undefined}
                                        statusContent={emailErrors[frequency] ? setErrors(frequency) : null}
                                        label={t('emailIds')}
                                        onChange={(e: any) => {
                                            setEmails({ ...emails, [frequency]: e.target.value });
                                            setEmailErrors({ ...emailErrors, [frequency]: '' });
                                        }}
                                        onBlur={() => handleOnBlur(frequency)}
                                        value={emails[frequency]}
                                        className={Styles.taWhitelistC}
                                    />
                                    {
                                        !emailErrors[frequency] &&
                                        <div className={Styles.dF}>
                                            <img
                                                src={infoIcon}
                                                alt="info"
                                                className={Styles.infoIcon}
                                            />
                                            <div className={Styles.emailsTextArea}>{t('BLD00141')}</div>
                                        </div>
                                    }
                                </> : null}
                        </div>
                    );
                })}

            </>
        )
    };

    return (
        <Slider
            containerId="root-modal"
            onClose={() => { onClose(); onLoad(); }}
            visibility={sidebarVisibility}
            footer={getFooter()}
            label={t(REPORT_KEY + '_title')}
        >
            {getContent()}
        </Slider>
    );
};

export default BillingAlertConfig;